import React from "react";

const S = {
  wrapper: { marginTop:'10px', marginLeft:'0px', border:'3px', padding:'40px', borderStyle:'solid', borderColor:'grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1: { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #2E75B6', paddingBottom:'8px', marginBottom:'24px', color:'#1F3864' },
  h2: { fontSize:'1.3rem', fontWeight:'700', color:'#2E75B6', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #2E75B6', paddingLeft:'10px' },
  p: { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox: { background:'#EBF3FB', border:'2px solid #2E75B6', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#1F3864', marginBottom:'8px' },
  infoBoxItem: { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #2E75B6' },
  table: { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th: { background:'#2E75B6', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE: { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#f5f9ff' },
  tdO: { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#fff' },
  tdL: { fontWeight:'600', color:'#1F3864', whiteSpace:'nowrap', width:'220px' },
  code: { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap: { margin:'24px 0', background:'#f8fbff', border:'1px solid #ccd9ea', borderRadius:'8px', padding:'20px' },
  dTitle: { fontWeight:'700', fontSize:'0.95rem', color:'#1F3864', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption: { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Formula / Notes</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 1 — Riemann Sums: Left, Right, Midpoint
// ─────────────────────────────────────────────────────────────────
function DiagramRiemannSums() {
  const W = 900, H = 320;

  // f(x) = 0.4x² + 0.5  on [0, 4]
  const f = x => 0.4 * x * x + 0.5;
  const a = 0, b = 4, n = 6;
  const dx = (b - a) / n;

  // Three panels side-by-side
  const panels = [
    { label: 'Left Riemann Sum', xi: i => a + i * dx,       color: '#2E75B6', fill: '#d0e8ff', note: 'Uses left endpoint of each subinterval' },
    { label: 'Right Riemann Sum', xi: i => a + (i+1) * dx,  color: '#c0392b', fill: '#fde8e8', note: 'Uses right endpoint of each subinterval' },
    { label: 'Midpoint Rule',     xi: i => a + (i+0.5)*dx,  color: '#27ae60', fill: '#d0f0d8', note: 'Uses midpoint — most accurate of the three' },
  ];

  const panW = (W - 20) / 3 - 5;
  const pOx  = pw => pw + 30;     // local origin x within panel
  const pOy  = 260;
  const scX  = 52, scY = 52;
  const tX = (px, x) => px + 30 + x * scX;
  const tY = y => pOy - y * scY;

  // true area via simple trapezoidal (visual reference only)
  // ∫₀⁴ (0.4x²+0.5)dx = [0.4x³/3 + 0.5x]₀⁴ = 0.4*64/3 + 2 ≈ 10.53
  const trueArea = (0.4 * 64 / 3 + 0.5 * 4).toFixed(2);

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Riemann Sums — Approximating Area with Rectangles (n = {n} subintervals)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        {panels.map(({ label, xi, color, fill, note }, pi) => {
          const px0 = 10 + pi * (panW + 8);
          const localToX = x => px0 + 30 + x * scX;

          // Rectangles
          const rects = Array.from({length: n}, (_, i) => {
            const xL = a + i * dx;
            const xR = xL + dx;
            const h  = f(xi(i));
            return { xL, xR, h };
          });

          // Riemann sum value
          const sum = rects.reduce((acc, r) => acc + r.h * dx, 0).toFixed(2);

          // Curve points clipped to panel
          const curvePts = [];
          for (let x = 0; x <= 4.05; x += 0.06) {
            curvePts.push(`${localToX(x).toFixed(1)},${tY(f(x)).toFixed(1)}`);
          }

          return (
            <g key={pi}>
              {/* Panel background */}
              <rect x={px0} y={10} width={panW} height={H - 20} rx={5} fill="#fff" stroke="#ccd9ea"/>
              {/* Header */}
              <rect x={px0 + 4} y={14} width={panW - 8} height={26} rx={3} fill={color}/>
              <text x={px0 + panW/2} y={31} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">{label}</text>

              {/* Rectangles (drawn before curve so curve sits on top) */}
              {rects.map(({ xL, xR, h }, i) => (
                <rect
                  key={i}
                  x={localToX(xL)} y={tY(h)}
                  width={Math.max(0, localToX(xR) - localToX(xL) - 0.5)}
                  height={tY(0) - tY(h)}
                  fill={fill} stroke={color} strokeWidth="1"
                />
              ))}

              {/* Curve */}
              <polyline points={curvePts.join(' ')} fill="none" stroke="#1F3864" strokeWidth="2.2"/>

              {/* Axes */}
              <line x1={localToX(-0.1)} y1={tY(0)} x2={localToX(4.2)} y2={tY(0)} stroke="#1F3864" strokeWidth="1.4"/>
              <line x1={localToX(0)} y1={tY(0)+6} x2={localToX(0)} y2={tY(4.5)} stroke="#1F3864" strokeWidth="1.4"/>

              {/* x-axis ticks */}
              {[0,1,2,3,4].map(v => (
                <g key={v}>
                  <line x1={localToX(v)} y1={tY(0)} x2={localToX(v)} y2={tY(0)+5} stroke="#888" strokeWidth="1"/>
                  <text x={localToX(v)} y={tY(0)+16} textAnchor="middle" fontSize="10" fill="#888">{v}</text>
                </g>
              ))}

              {/* Sample evaluation point dot */}
              {rects.map(({ h }, i) => (
                <circle key={i} cx={localToX(xi(i))} cy={tY(h)} r={3} fill={color} stroke="#fff" strokeWidth="1"/>
              ))}

              {/* Sum result */}
              <rect x={px0+8} y={tY(0)+24} width={panW-16} height={38} rx={3} fill={fill} stroke={color} strokeWidth="1"/>
              <text x={px0+panW/2} y={tY(0)+38} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">
                Σ ≈ {sum}
              </text>
              <text x={px0+panW/2} y={tY(0)+52} textAnchor="middle" fontSize="10" fill="#555">
                (true area ≈ {trueArea})
              </text>

              {/* Note */}
              <text x={px0+panW/2} y={H-14} textAnchor="middle" fontSize="10" fill="#777" fontStyle="italic">{note}</text>
            </g>
          );
        })}

        {/* Convergence arrow label across all three */}
        <text x={W/2} y={H - 30} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">
          As n → ∞, all three sums converge to the exact definite integral
        </text>
      </svg>
      <div style={S.dCaption}>
        A Riemann sum approximates the area under a curve by filling it with n rectangles of width Δx = (b−a)/n. The height of each rectangle is f evaluated at a chosen x in that strip. As n → ∞ and Δx → 0, every Riemann sum converges to the same value — the definite integral.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 2 — The Fundamental Theorem of Calculus
// ─────────────────────────────────────────────────────────────────
function DiagramFundamentalTheorem() {
  const W = 900, H = 340;

  // f(x) = sin(x) + 1.2  on [0, 2π], F(x) = -cos(x) + 1.2x
  const f  = x => Math.sin(x) + 1.2;
  const F  = x => -Math.cos(x) + 1.2 * x;

  const ox1 = 55, oy1 = 220, sc1X = 68, sc1Y = 68;
  const toX1 = x => ox1 + x * sc1X;
  const toY1 = y => oy1 - y * sc1Y;

  const ox2 = 530, oy2 = 220, sc2X = 68, sc2Y = 22;
  const toX2 = x => ox2 + x * sc2X;
  const toY2 = y => oy2 - y * sc2Y;

  // Shaded area region a=0.5 to b=2.5
  const aShade = 0.8, bShade = 2.8;
  const shadeSteps = 60;
  const shadeArea = (() => {
    const pts = [`${toX1(aShade).toFixed(1)},${toY1(0).toFixed(1)}`];
    for (let i = 0; i <= shadeSteps; i++) {
      const x = aShade + (bShade - aShade) * i / shadeSteps;
      pts.push(`${toX1(x).toFixed(1)},${toY1(f(x)).toFixed(1)}`);
    }
    pts.push(`${toX1(bShade).toFixed(1)},${toY1(0).toFixed(1)}`);
    return pts.join(' ');
  })();

  // Curve 1 points
  const curve1Pts = [];
  for (let x = 0; x <= Math.PI * 2 + 0.05; x += 0.05) {
    curve1Pts.push(`${toX1(x).toFixed(1)},${toY1(f(x)).toFixed(1)}`);
  }

  // Curve 2 (antiderivative F) points
  const curve2Pts = [];
  for (let x = 0; x <= Math.PI * 2 + 0.05; x += 0.05) {
    const sy = toY2(F(x));
    if (sy > 14 && sy < H - 14) curve2Pts.push(`${toX2(x).toFixed(1)},${sy.toFixed(1)}`);
  }

  const exactArea = (F(bShade) - F(aShade)).toFixed(3);

  const piTicks1 = [[0,'0'],[Math.PI/2,'π/2'],[Math.PI,'π'],[3*Math.PI/2,'3π/2'],[2*Math.PI,'2π']];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>The Fundamental Theorem of Calculus — Linking Integration and Differentiation</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* ── LEFT PANEL: definite integral as area ── */}
        <rect x={10} y={10} width={450} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={235} y={30} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">FTC Part 2: ∫ₐᵇ f(x) dx = F(b) − F(a)</text>

        {/* Shaded area */}
        <polygon points={shadeArea} fill="#d0e8ff" opacity="0.7"/>
        <polyline points={shadeArea} fill="none"/>

        {/* Curve f(x) */}
        <polyline points={curve1Pts.join(' ')} fill="none" stroke="#2E75B6" strokeWidth="2.8"/>
        <text x={toX1(1.5)} y={toY1(f(1.5))-12} fontSize="13" fill="#2E75B6" fontWeight="700">f(x) = sin x + 1.2</text>

        {/* Axes */}
        <line x1={toX1(-0.1)} y1={oy1} x2={toX1(2*Math.PI+0.3)} y2={oy1} stroke="#1F3864" strokeWidth="1.5"/>
        <line x1={ox1} y1={toY1(2.4)} x2={ox1} y2={toY1(-0.3)} stroke="#1F3864" strokeWidth="1.5"/>
        <text x={toX1(2*Math.PI+0.35)} y={oy1+4} fontSize="12" fill="#1F3864">x</text>
        <text x={ox1+4} y={toY1(2.4)-4} fontSize="12" fill="#1F3864">y</text>

        {/* x ticks */}
        {piTicks1.map(([t,lbl]) => (
          <g key={lbl}>
            <line x1={toX1(t)} y1={oy1-4} x2={toX1(t)} y2={oy1+4} stroke="#888" strokeWidth="1.2"/>
            <text x={toX1(t)} y={oy1+16} textAnchor="middle" fontSize="10" fill="#888">{lbl}</text>
          </g>
        ))}

        {/* a and b markers */}
        <line x1={toX1(aShade)} y1={toY1(0)} x2={toX1(aShade)} y2={toY1(f(aShade))} stroke="#c0392b" strokeWidth="1.5" strokeDasharray="5,3"/>
        <line x1={toX1(bShade)} y1={toY1(0)} x2={toX1(bShade)} y2={toY1(f(bShade))} stroke="#c0392b" strokeWidth="1.5" strokeDasharray="5,3"/>
        <text x={toX1(aShade)} y={oy1+26} textAnchor="middle" fontSize="12" fill="#c0392b" fontWeight="700">a</text>
        <text x={toX1(bShade)} y={oy1+26} textAnchor="middle" fontSize="12" fill="#c0392b" fontWeight="700">b</text>

        {/* Area label */}
        <text x={(toX1(aShade)+toX1(bShade))/2} y={toY1(0.55)} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Area</text>
        <text x={(toX1(aShade)+toX1(bShade))/2} y={toY1(0.28)} textAnchor="middle" fontSize="11" fill="#2E75B6" fontFamily="'Courier New',monospace">= {exactArea}</text>

        {/* FTC formula box */}
        <rect x={18} y={H-86} width={434} height={70} rx={4} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1.5"/>
        <text x={235} y={H-70} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">FTC Part 1:  d/dx [∫ₐˣ f(t) dt] = f(x)</text>
        <text x={235} y={H-52} textAnchor="middle" fontSize="11" fill="#555">If F is any antiderivative of f  (F' = f), then:</text>
        <text x={235} y={H-34} textAnchor="middle" fontSize="13" fill="#2E75B6" fontFamily="'Courier New',monospace" fontWeight="700">∫ₐᵇ f(x) dx  =  F(b) − F(a)</text>

        {/* ── RIGHT PANEL: antiderivative ── */}
        <rect x={468} y={10} width={422} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={679} y={30} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">F(x) = −cos x + 1.2x  is the antiderivative of f(x)</text>

        {/* F(x) curve */}
        <polyline points={curve2Pts.join(' ')} fill="none" stroke="#c0392b" strokeWidth="2.8"/>
        <text x={toX2(1.1)} y={toY2(F(1.1))-12} fontSize="13" fill="#c0392b" fontWeight="700">F(x)</text>

        {/* Axes */}
        <line x1={toX2(-0.1)} y1={oy2} x2={toX2(2*Math.PI+0.3)} y2={oy2} stroke="#1F3864" strokeWidth="1.5"/>
        <line x1={ox2} y1={H-18} x2={ox2} y2={20} stroke="#1F3864" strokeWidth="1.5"/>
        <text x={toX2(2*Math.PI+0.35)} y={oy2+4} fontSize="12" fill="#1F3864">x</text>

        {piTicks1.map(([t,lbl]) => (
          <g key={lbl}>
            <line x1={toX2(t)} y1={oy2-4} x2={toX2(t)} y2={oy2+4} stroke="#888" strokeWidth="1.2"/>
            <text x={toX2(t)} y={oy2+16} textAnchor="middle" fontSize="10" fill="#888">{lbl}</text>
          </g>
        ))}

        {/* F(a) and F(b) dots + vertical drop */}
        {[aShade, bShade].map((xv, i) => (
          <g key={i}>
            <line x1={toX2(xv)} y1={toY2(0)} x2={toX2(xv)} y2={toY2(F(xv))} stroke="#9b59b6" strokeWidth="1.5" strokeDasharray="4,3"/>
            <circle cx={toX2(xv)} cy={toY2(F(xv))} r={5} fill="#9b59b6" stroke="#fff" strokeWidth="1.5"/>
            <text x={toX2(xv)+(i===0?-28:8)} y={toY2(F(xv))-8} fontSize="11" fill="#9b59b6" fontWeight="700">F({i===0?'a':'b'})</text>
          </g>
        ))}

        {/* F(b)-F(a) brace */}
        <line x1={toX2(bShade)+20} y1={toY2(F(aShade))} x2={toX2(bShade)+20} y2={toY2(F(bShade))} stroke="#27ae60" strokeWidth="2"/>
        <line x1={toX2(bShade)+15} y1={toY2(F(aShade))} x2={toX2(bShade)+25} y2={toY2(F(aShade))} stroke="#27ae60" strokeWidth="1.5"/>
        <line x1={toX2(bShade)+15} y1={toY2(F(bShade))} x2={toX2(bShade)+25} y2={toY2(F(bShade))} stroke="#27ae60" strokeWidth="1.5"/>
        <text x={toX2(bShade)+28} y={(toY2(F(aShade))+toY2(F(bShade)))/2+4} fontSize="12" fill="#27ae60" fontWeight="700">F(b)−F(a)</text>
        <text x={toX2(bShade)+28} y={(toY2(F(aShade))+toY2(F(bShade)))/2+18} fontSize="11" fill="#27ae60">= {exactArea}</text>

        {/* Derivative reminder */}
        <rect x={476} y={H-86} width={406} height={70} rx={4} fill="#fff0f0" stroke="#c0392b" strokeWidth="1.5"/>
        <text x={679} y={H-70} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">The two parts of the FTC are inverses:</text>
        <text x={679} y={H-52} textAnchor="middle" fontSize="11" fill="#c0392b" fontFamily="'Courier New',monospace">Differentiation  ←→  Integration</text>
        <text x={679} y={H-34} textAnchor="middle" fontSize="11" fill="#555">F'(x) = f(x)  ⟺  ∫f(x)dx = F(x) + C</text>
      </svg>
      <div style={S.dCaption}>
        The Fundamental Theorem has two parts. Part 1: the derivative of an integral (with variable upper limit) recovers the integrand. Part 2: to evaluate a definite integral, find any antiderivative F and compute F(b)−F(a). The shaded area on the left equals the vertical rise F(b)−F(a) on the right.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 3 — Standard Integrals Table
// ─────────────────────────────────────────────────────────────────
function DiagramStandardIntegrals() {
  const W = 900, H = 360;

  const col1 = [
    { fn:'∫ xⁿ dx',       res:'xⁿ⁺¹/(n+1) + C',    note:'n ≠ −1',          color:'#2E75B6' },
    { fn:'∫ 1/x dx',      res:'ln|x| + C',           note:'x ≠ 0',           color:'#2E75B6' },
    { fn:'∫ eˣ dx',       res:'eˣ + C',              note:'unique fixed point', color:'#c0392b' },
    { fn:'∫ aˣ dx',       res:'aˣ/ln(a) + C',        note:'a > 0, a ≠ 1',   color:'#c0392b' },
    { fn:'∫ ln x dx',     res:'x·ln x − x + C',      note:'integration by parts', color:'#c0392b' },
    { fn:'∫ sin x dx',    res:'−cos x + C',           note:'sign flips',      color:'#9b59b6' },
    { fn:'∫ cos x dx',    res:'sin x + C',            note:'',                color:'#9b59b6' },
  ];
  const col2 = [
    { fn:'∫ tan x dx',    res:'−ln|cos x| + C',      note:'= ln|sec x| + C', color:'#9b59b6' },
    { fn:'∫ sec² x dx',   res:'tan x + C',           note:'derivative of tan', color:'#e07b00' },
    { fn:'∫ csc² x dx',   res:'−cot x + C',          note:'derivative of −cot', color:'#e07b00' },
    { fn:'∫ sec x tan x dx', res:'sec x + C',         note:'',               color:'#e07b00' },
    { fn:'∫ 1/√(1−x²) dx', res:'arcsin x + C',       note:'|x| < 1',        color:'#27ae60' },
    { fn:'∫ 1/(1+x²) dx', res:'arctan x + C',        note:'very common',     color:'#27ae60' },
    { fn:'∫ 1/√(a²−x²) dx', res:'arcsin(x/a) + C',  note:'a > 0',          color:'#27ae60' },
  ];

  const rowH = 38, headerH = 42, tableTop = 52;
  const col1X = 14, col2X = 458, colW = 428;
  const fnW = 190, resW = 155, noteW = colW - fnW - resW - 6;

  const renderTable = (entries, xOff) => (
    <g>
      {/* Header */}
      <rect x={xOff} y={tableTop} width={colW} height={headerH} rx={4} fill="#1F3864"/>
      <text x={xOff + fnW/2}          y={tableTop+16} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">Integrand</text>
      <text x={xOff + fnW + resW/2}   y={tableTop+16} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">Antiderivative</text>
      <text x={xOff + fnW + resW + noteW/2} y={tableTop+16} textAnchor="middle" fontSize="11" fill="#aac8e8" fontWeight="700">Note</text>
      <text x={xOff + fnW/2}          y={tableTop+32} textAnchor="middle" fontSize="10" fill="#aac8e8">f(x)</text>
      <text x={xOff + fnW + resW/2}   y={tableTop+32} textAnchor="middle" fontSize="10" fill="#aac8e8">∫f(x) dx</text>

      {/* Rows */}
      {entries.map(({ fn, res, note, color }, i) => {
        const ry = tableTop + headerH + i * rowH;
        return (
          <g key={fn}>
            <rect x={xOff} y={ry} width={colW} height={rowH} fill={i%2===0?'#f8fbff':'#fff'} stroke="#eee" strokeWidth="0.5"/>
            {/* left accent bar */}
            <rect x={xOff} y={ry} width={4} height={rowH} fill={color}/>
            <text x={xOff+14} y={ry+rowH/2+5} fontSize="12" fill={color} fontFamily="'Courier New',monospace" fontWeight="700">{fn}</text>
            <text x={xOff+fnW+8} y={ry+rowH/2+5} fontSize="12" fill="#1F3864" fontFamily="'Courier New',monospace">{res}</text>
            <text x={xOff+fnW+resW+6} y={ry+rowH/2+5} fontSize="10" fill="#888" fontStyle="italic">{note}</text>
          </g>
        );
      })}
      {/* Bottom border */}
      <rect x={xOff} y={tableTop + headerH + entries.length * rowH} width={colW} height={1} fill="#ccd9ea"/>
    </g>
  );

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Standard Integrals — The Essential Antiderivative Table</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        {/* Top banner */}
        <rect x={14} y={10} width={W-28} height={34} rx={4} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1.2"/>
        <text x={W/2} y={23} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">Every antiderivative formula can be verified by differentiation:  d/dx [F(x) + C] = f(x)</text>
        <text x={W/2} y={38} textAnchor="middle" fontSize="11" fill="#555">The constant C represents any vertical shift — it disappears in definite integrals</text>

        {renderTable(col1, col1X)}
        {renderTable(col2, col2X)}
      </svg>
      <div style={S.dCaption}>
        These 14 entries form the core antiderivative table. Every entry can be confirmed by differentiating the right-hand side. The constant +C is mandatory for indefinite integrals — it encodes the fact that infinitely many antiderivatives differ only by a vertical shift.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 4 — Integration Techniques
// ─────────────────────────────────────────────────────────────────
function DiagramTechniques() {
  const W = 900, H = 360;

  const techniques = [
    {
      name: 'u-Substitution',
      color: '#2E75B6', fill: '#EBF3FB',
      rule: '∫ f(g(x))·g\'(x) dx = ∫ f(u) du',
      when: 'Function inside a function; inner derivative present',
      steps: ['Let u = g(x) (the inner function)', 'Compute du = g\'(x) dx', 'Rewrite integral entirely in u', 'Integrate, then back-substitute'],
      ex: '∫ 2x·cos(x²) dx',
      sol: 'u=x², du=2x dx\n= ∫cos(u)du = sin(u)+C\n= sin(x²) + C',
    },
    {
      name: 'Integration by Parts',
      color: '#c0392b', fill: '#fff0f0',
      rule: '∫ u dv = uv − ∫ v du',
      when: 'Product of two unrelated types (poly × trig, poly × ln)',
      steps: ['Choose u and dv (LIATE order)', 'Compute du = u\' dx, find v = ∫dv', 'Apply formula: uv − ∫v du', 'May need to repeat or solve for integral'],
      ex: '∫ x·eˣ dx',
      sol: 'u=x, dv=eˣdx\ndu=dx, v=eˣ\n= xeˣ − ∫eˣdx = xeˣ−eˣ+C',
    },
    {
      name: 'Partial Fractions',
      color: '#27ae60', fill: '#f0fff4',
      rule: 'P(x)/Q(x) = A/(x−r₁) + B/(x−r₂) + …',
      when: 'Rational function; degree of numerator < denominator',
      steps: ['Factor the denominator completely', 'Write partial fraction decomposition', 'Multiply through & solve for A, B, …', 'Integrate each simple term'],
      ex: '∫ 1/(x²−1) dx',
      sol: '= ∫ [½·1/(x−1) − ½·1/(x+1)]dx\n= ½ln|x−1|−½ln|x+1|+C',
    },
    {
      name: 'Trig Substitution',
      color: '#9b59b6', fill: '#f5d0ff',
      rule: 'Replace x with a trig function to remove radicals',
      when: '√(a²−x²), √(a²+x²), or √(x²−a²) appear',
      steps: ['√(a²−x²): x = a·sin θ', '√(a²+x²): x = a·tan θ', '√(x²−a²): x = a·sec θ', 'Use trig identity, integrate, back-sub'],
      ex: '∫ √(1−x²) dx',
      sol: 'x=sin θ, dx=cos θ dθ\n= ∫cos²θ dθ = θ/2+sin(2θ)/4+C\n= ½arcsin(x)+½x√(1−x²)+C',
    },
  ];

  const panW = (W - 20) / 4 - 5;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Integration Techniques — u-Substitution, By Parts, Partial Fractions, Trig Substitution</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        {techniques.map(({ name, color, fill, rule, when, steps, ex, sol }, i) => {
          const px = 10 + i * (panW + 7);
          return (
            <g key={name}>
              {/* Panel */}
              <rect x={px} y={10} width={panW} height={H-20} rx={5} fill={fill} stroke={color} strokeWidth="1.8"/>
              {/* Header */}
              <rect x={px+4} y={14} width={panW-8} height={28} rx={3} fill={color}/>
              <text x={px+panW/2} y={32} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">{name}</text>

              {/* Rule box */}
              <rect x={px+6} y={50} width={panW-12} height={30} rx={3} fill="#fff" stroke={color} strokeWidth="1"/>
              <text x={px+panW/2} y={62} textAnchor="middle" fontSize="9.5" fill={color} fontFamily="'Courier New',monospace" fontWeight="700">{rule.split('\n')[0]}</text>
              {rule.split('\n')[1] && <text x={px+panW/2} y={75} textAnchor="middle" fontSize="9" fill={color} fontFamily="'Courier New',monospace">{rule.split('\n')[1]}</text>}

              {/* When to use */}
              <text x={px+8} y={98} fontSize="10" fill="#1F3864" fontWeight="700">When to use:</text>
              {when.split(';').map((w,j) => (
                <text key={j} x={px+8} y={112+j*13} fontSize="10" fill="#555">{w.trim()}</text>
              ))}

              {/* Steps */}
              <text x={px+8} y={148} fontSize="10" fill="#1F3864" fontWeight="700">Steps:</text>
              {steps.map((s, j) => (
                <g key={j}>
                  <circle cx={px+15} cy={160+j*17-4} r={6} fill={color}/>
                  <text x={px+15} y={160+j*17-0} textAnchor="middle" fontSize="9" fill="#fff" fontWeight="700">{j+1}</text>
                  <text x={px+26} y={160+j*17} fontSize="10" fill="#333">{s}</text>
                </g>
              ))}

              {/* Example */}
              <line x1={px+8} y1={230} x2={px+panW-8} y2={230} stroke="#ddd" strokeWidth="1"/>
              <text x={px+8} y={244} fontSize="10" fill="#1F3864" fontWeight="700">Example: {ex}</text>
              {sol.split('\n').map((ln, j) => (
                <text key={j} x={px+8} y={260+j*16} fontSize="10" fill={color} fontFamily="'Courier New',monospace">{ln}</text>
              ))}
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>
        Choosing the right technique is the main skill. u-Substitution reverses the chain rule — look for an inner function whose derivative appears. Integration by parts reverses the product rule — use LIATE (Logarithm, Inverse trig, Algebraic, Trig, Exponential) to pick u. Partial fractions decompose rational functions. Trig substitution eliminates square roots.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 5 — Area Between Curves
// ─────────────────────────────────────────────────────────────────
function DiagramAreaBetweenCurves() {
  const W = 900, H = 340;

  // f(x) = -0.4x²+3.2,  g(x) = 0.5x   on [-1, 5]
  // Intersection: -0.4x²+3.2 = 0.5x → 0.4x²+0.5x-3.2=0 → x = (-0.5 ± √(0.25+5.12))/0.8
  // x ≈ (-0.5 ± 2.317)/0.8 → x₁ ≈ -3.52, x₂ ≈ 2.27  (use x₁=-3.52 and x₂=2.27 for region)
  // Redefine simpler: f(x)=−x²+4, g(x)=x+2  → intersection: x²+x-2=0 → (x+2)(x−1)=0 → x=−2,1
  const f = x => -x*x + 4;
  const g = x => x + 2;
  const xL = -2, xR = 1;
  // Area = ∫₋₂¹ (f−g)dx = ∫₋₂¹ (−x²+4 − x − 2)dx = ∫₋₂¹ (−x²−x+2)dx
  // = [−x³/3 − x²/2 + 2x]₋₂¹ = (−1/3−1/2+2) − (8/3−2−4) = 7/6 − (−10/3) = 7/6+20/6 = 27/6 = 4.5

  const ox = 80, oy = 240, scX = 90, scY = 44;
  const toX = x => ox + (x + 2.5) * scX;
  const toY = y => oy - y * scY;

  // Shaded region polygon
  const shadeN = 80;
  const shadePts = (() => {
    const top = [], bot = [];
    for (let i = 0; i <= shadeN; i++) {
      const x = xL + (xR - xL) * i / shadeN;
      top.push(`${toX(x).toFixed(1)},${toY(f(x)).toFixed(1)}`);
      bot.unshift(`${toX(x).toFixed(1)},${toY(g(x)).toFixed(1)}`);
    }
    return [...top, ...bot].join(' ');
  })();

  // Curve pts
  const fPts = [], gPts = [];
  for (let x = -2.4; x <= 2.0; x += 0.05) {
    const sy = toY(f(x));
    if (sy > 10 && sy < H-10) fPts.push(`${toX(x).toFixed(1)},${sy.toFixed(1)}`);
    const sy2 = toY(g(x));
    if (sy2 > 10 && sy2 < H-10) gPts.push(`${toX(x).toFixed(1)},${sy2.toFixed(1)}`);
  }

  // Sample vertical strip at x=0
  const xSamp = -0.5;
  const fTop = toY(f(xSamp)), gBot = toY(g(xSamp));

  // Right panel
  const panX = 500, panW = W - panX - 16;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Area Between Curves — ∫ₐᵇ [f(x) − g(x)] dx  where f(x) ≥ g(x)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* Graph panel */}
        <rect x={10} y={10} width={480} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={250} y={30} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">f(x) = −x²+4  and  g(x) = x+2,  intersect at x = −2 and x = 1</text>

        {/* Shaded area */}
        <polygon points={shadePts} fill="#d0e8ff" opacity="0.7"/>

        {/* Curves */}
        <polyline points={fPts.join(' ')} fill="none" stroke="#2E75B6" strokeWidth="2.8"/>
        <polyline points={gPts.join(' ')} fill="none" stroke="#c0392b" strokeWidth="2.5"/>

        {/* Labels */}
        <text x={toX(-0.2)} y={toY(f(-0.2))-12} fontSize="13" fill="#2E75B6" fontWeight="700">f(x) = −x²+4</text>
        <text x={toX(1.0)+8} y={toY(g(1.0))+4} fontSize="13" fill="#c0392b" fontWeight="700">g(x) = x+2</text>

        {/* Axes */}
        <line x1={toX(-2.6)} y1={oy} x2={toX(2.1)} y2={oy} stroke="#1F3864" strokeWidth="1.5"/>
        <line x1={ox} y1={toY(4.6)} x2={ox} y2={toY(-2)} stroke="#1F3864" strokeWidth="1.5"/>
        <text x={toX(2.1)+4} y={oy+4} fontSize="12" fill="#1F3864">x</text>
        <text x={ox+4} y={toY(4.6)-4} fontSize="12" fill="#1F3864">y</text>

        {/* Ticks */}
        {[-2,-1,0,1,2].map(v => (
          <g key={v}>
            <line x1={toX(v)} y1={oy-4} x2={toX(v)} y2={oy+4} stroke="#888" strokeWidth="1.2"/>
            <text x={toX(v)} y={oy+16} textAnchor="middle" fontSize="11" fill="#aaa">{v}</text>
          </g>
        ))}
        {[1,2,3,4].map(v => (
          <text key={v} x={ox-8} y={toY(v)+4} textAnchor="end" fontSize="11" fill="#aaa">{v}</text>
        ))}

        {/* Intersection points */}
        {[xL, xR].map(xv => (
          <g key={xv}>
            <circle cx={toX(xv)} cy={toY(f(xv))} r={6} fill="#27ae60" stroke="#fff" strokeWidth="2"/>
            <text x={toX(xv)+(xv<0?-40:8)} y={toY(f(xv))-10} fontSize="11" fill="#27ae60" fontWeight="700">x={xv}</text>
          </g>
        ))}

        {/* Sample vertical strip */}
        <line x1={toX(xSamp)} y1={fTop} x2={toX(xSamp)} y2={gBot} stroke="#e07b00" strokeWidth="3"/>
        <line x1={toX(xSamp)-10} y1={fTop} x2={toX(xSamp)+10} y2={fTop} stroke="#e07b00" strokeWidth="1.5"/>
        <line x1={toX(xSamp)-10} y1={gBot} x2={toX(xSamp)+10} y2={gBot} stroke="#e07b00" strokeWidth="1.5"/>
        <text x={toX(xSamp)+12} y={(fTop+gBot)/2+4} fontSize="11" fill="#e07b00" fontWeight="700">f − g</text>

        {/* Area result */}
        <text x={toX((xL+xR)/2)} y={toY((f((xL+xR)/2)+g((xL+xR)/2))/2)} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">A = 4.5</text>

        {/* Right panel */}
        <rect x={panX} y={10} width={panW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={panX+panW/2} y={30} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Method</text>

        {[
          { y:40,  bg:'#EBF3FB', bc:'#2E75B6',
            lines:['Step 1: Find intersections', 'Set f(x) = g(x), solve for x', 'These are the limits a and b'] },
          { y:116, bg:'#fff7ee', bc:'#e07b00',
            lines:['Step 2: Verify which is on top', 'Check a point in [a,b]', 'Top curve − Bottom curve ≥ 0'] },
          { y:192, bg:'#f0fff4', bc:'#27ae60',
            lines:['Step 3: Integrate the gap', 'A = ∫ₐᵇ [f(x)−g(x)] dx', 'Use FTC: F(b)−F(a)'] },
          { y:268, bg:'#fff0f0', bc:'#c0392b',
            lines:['If curves cross inside [a,b]:', 'Split into sub-intervals', 'Always subtract (lower) from (upper)'] },
        ].map(({y,bg,bc,lines}) => (
          <g key={y}>
            <rect x={panX+8} y={y} width={panW-16} height={70} rx={3} fill={bg} stroke={bc} strokeWidth="1.2"/>
            {lines.map((ln,i) => (
              <text key={i} x={panX+16} y={y+18+i*18} fontSize="12"
                fill={i===0?bc:i===1?'#1F3864':'#555'}
                fontWeight={i===0?'700':'400'}
                fontFamily={i===0?"'Courier New',monospace":"'Georgia',serif"}>{ln}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        The area between two curves is the integral of their vertical gap. Find where they intersect (limits a and b), verify which function is on top in that interval, then integrate f(x)−g(x). If the curves cross within [a,b], split the interval and integrate each piece separately (always upper minus lower).
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 6 — Applications: Volume, Arc Length, Physics
// ─────────────────────────────────────────────────────────────────
function DiagramApplications() {
  const W = 900, H = 330;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Applications of Integration — Volume of Revolution, Arc Length, Physics</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* Top header */}
        <rect x={14} y={10} width={W-28} height={38} rx={5} fill="#1F3864"/>
        <text x={W/2} y={26} textAnchor="middle" fontSize="14" fill="#fff" fontWeight="700">Integration computes any quantity that can be built up as a sum of infinitely thin slices</text>
        <text x={W/2} y={42} textAnchor="middle" fontSize="11" fill="#aac8e8">The key step: express the quantity for one thin slice, then integrate over the full range</text>

        {/* Six application cards */}
        {[
          {
            x:14, w:280, color:'#2E75B6', fill:'#EBF3FB',
            title:'Volume — Disk Method',
            formula:'V = π ∫ₐᵇ [f(x)]² dx',
            desc:'Rotate y=f(x) around x-axis.\nEach slice is a disk of radius f(x)\nand thickness dx.',
            example:'y=√x, [0,4] around x-axis:\nV = π∫₀⁴ x dx = π[x²/2]₀⁴ = 8π',
          },
          {
            x:304, w:280, color:'#c0392b', fill:'#fff0f0',
            title:'Volume — Shell Method',
            formula:'V = 2π ∫ₐᵇ x·f(x) dx',
            desc:'Rotate around y-axis.\nEach slice is a cylindrical shell\nof radius x and height f(x).',
            example:'y=x², [0,2] around y-axis:\nV = 2π∫₀² x·x² dx = 2π[x⁴/4]₀² = 8π',
          },
          {
            x:594, w:292, color:'#27ae60', fill:'#f0fff4',
            title:'Arc Length',
            formula:'L = ∫ₐᵇ √(1 + [f\'(x)]²) dx',
            desc:'Length of curve y=f(x) from a to b.\nEach tiny segment has length\n√(dx²+dy²) = √(1+m²) dx.',
            example:'y=x^(3/2), [0,4]:\nL = ∫₀⁴ √(1+9x/4) dx ≈ 9.07',
          },
        ].map(({x,w,color,fill,title,formula,desc,example}) => (
          <g key={title}>
            <rect x={x} y={58} width={w} height={130} rx={5} fill={fill} stroke={color} strokeWidth="1.5"/>
            <rect x={x+4} y={62} width={w-8} height={24} rx={3} fill={color}/>
            <text x={x+w/2} y={78} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">{title}</text>
            <rect x={x+8} y={92} width={w-16} height={22} rx={2} fill="#fff" stroke={color} strokeWidth="1"/>
            <text x={x+w/2} y={107} textAnchor="middle" fontSize="12" fill={color} fontFamily="'Courier New',monospace" fontWeight="700">{formula}</text>
            {desc.split('\n').map((ln,j) => (
              <text key={j} x={x+10} y={124+j*14} fontSize="10" fill="#444">{ln}</text>
            ))}
          </g>
        ))}

        {/* Physics / Engineering applications — bottom row */}
        {[
          {
            x:14, w:198, color:'#9b59b6', fill:'#f5d0ff',
            title:'Work', formula:'W = ∫ F(x) dx',
            lines:['Force varies with position.','Each slice: dW = F(x) dx','Sum all slices from a to b.'],
            ex:'Spring: F=kx → W=½kx²',
          },
          {
            x:220, w:198, color:'#e07b00', fill:'#fff7ee',
            title:'Center of Mass', formula:'x̄ = ∫x·ρ dx / ∫ρ dx',
            lines:['ρ = density (mass per unit length).','Numerator = first moment.','Denominator = total mass.'],
            ex:'Uniform rod: x̄ = L/2',
          },
          {
            x:426, w:198, color:'#2E75B6', fill:'#EBF3FB',
            title:'Probability', formula:'P(a≤X≤b) = ∫ₐᵇ f(x) dx',
            lines:['f(x) = probability density fn.','Total area under f = 1.','CDF: F(x) = ∫₋∞ˣ f(t) dt'],
            ex:'Normal, uniform, exponential',
          },
          {
            x:632, w:198, color:'#c0392b', fill:'#fff0f0',
            title:'Average Value', formula:'f̄ = 1/(b−a) ∫ₐᵇ f(x) dx',
            lines:['Mean value of f on [a,b].','MVT for integrals: some c','satisfies f(c) = f̄.'],
            ex:'Mean temperature over a day',
          },
          {
            x:838, w:48, color:'#aaa', fill:'#f8f8f8',
            title:'…', formula:'',
            lines:['+ many','more'],
            ex:'',
          },
        ].map(({x,w,color,fill,title,formula,lines,ex}) => (
          <g key={title}>
            <rect x={x} y={200} width={w} height={120} rx={4} fill={fill} stroke={color} strokeWidth="1.2"/>
            <rect x={x+3} y={203} width={w-6} height={20} rx={2} fill={color}/>
            <text x={x+w/2} y={217} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">{title}</text>
            {formula && <text x={x+w/2} y={233} textAnchor="middle" fontSize="10" fill={color} fontFamily="'Courier New',monospace" fontWeight="700">{formula}</text>}
            {lines.map((ln,j) => (
              <text key={j} x={x+6} y={248+j*13} fontSize="9.5" fill="#444">{ln}</text>
            ))}
            {ex && <text x={x+6} y={308} fontSize="9" fill="#888" fontStyle="italic">{ex}</text>}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        Integration generates volume by summing disk or shell slices; arc length by summing infinitesimal chord lengths; work by summing force × displacement; probability by summing density over an interval. The unifying idea is always the same: express what one thin slice contributes, then integrate to accumulate the total.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────
export default function Integrals(props) {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Integrals</h1>

      <p style={S.p}>
        Integration is the inverse of differentiation and the mathematical tool for computing accumulated quantities — areas, volumes, lengths, work, probability, and more. Where a derivative asks "how fast is this changing right now?", an integral asks "how much has accumulated in total?" The two operations are linked by the Fundamental Theorem of Calculus, which is arguably the most important result in all of mathematics.
      </p>

      <InfoBox
        title="Core Idea: Integration is Summation in the Limit"
        items={[
          "Indefinite integral: ∫f(x) dx = F(x) + C,  where F'(x) = f(x)  — the antiderivative",
          "Definite integral: ∫ₐᵇ f(x) dx = F(b) − F(a)  — net signed area under the curve",
          "Riemann sum: Σ f(xᵢ)·Δx → ∫f(x) dx  as n → ∞ and Δx → 0",
          "FTC: d/dx [∫ₐˣ f(t) dt] = f(x)  — differentiation undoes integration",
          "Linearity: ∫[af + bg] dx = a∫f dx + b∫g dx  — integrate term by term",
        ]}
      />

      <h2 style={S.h2}>Riemann Sums — Building the Integral from Rectangles</h2>
      <DiagramRiemannSums />
      <Table2 rows={[
        ["Partition",         "[a,b] divided into n subintervals each of width Δx = (b−a)/n\nAs n→∞, Δx→0 and the approximation becomes exact"],
        ["Left sum",          "Lₙ = Σᵢ f(xᵢ) · Δx,  xᵢ = a + i·Δx\nUnderestimates for increasing f, overestimates for decreasing"],
        ["Right sum",         "Rₙ = Σᵢ f(xᵢ₊₁) · Δx,  xᵢ₊₁ = a + (i+1)·Δx\nOpposite error to the left sum"],
        ["Midpoint rule",     "Mₙ = Σᵢ f((xᵢ+xᵢ₊₁)/2) · Δx\nGenerally the most accurate of the three simple rules"],
        ["Trapezoidal rule",  "Tₙ = (Δx/2)[f(x₀)+2f(x₁)+…+2f(xₙ₋₁)+f(xₙ)]\nAverages left and right; error O(Δx²)"],
        ["Simpson's rule",    "Sₙ = (Δx/3)[f(x₀)+4f(x₁)+2f(x₂)+…+4f(xₙ₋₁)+f(xₙ)]\nFits parabolas to pairs of strips; error O(Δx⁴)"],
      ]} />

      <h2 style={S.h2}>The Fundamental Theorem of Calculus</h2>
      <DiagramFundamentalTheorem />
      <Table2 rows={[
        ["FTC Part 1",        "d/dx [∫ₐˣ f(t) dt] = f(x)\nThe integral with variable upper limit is an antiderivative of f"],
        ["FTC Part 2",        "∫ₐᵇ f(x) dx = F(b) − F(a),  where F' = f\nEvaluate by finding any antiderivative and subtracting"],
        ["Net signed area",   "Positive where f > 0, negative where f < 0\nNet area can be zero even if total area is large"],
        ["Total area",        "∫ₐᵇ |f(x)| dx  — split at zeros of f\nAlways non-negative; use when asking 'how much total'"],
        ["Antiderivative",    "F is an antiderivative of f if F'(x) = f(x)\nAll antiderivatives differ by a constant: F(x) + C"],
        ["Properties",        "∫ₐᵃ f dx = 0;  ∫ₐᵇ f dx = −∫ᵦᵃ f dx\n∫ₐᵇ f dx + ∫ᵦᶜ f dx = ∫ₐᶜ f dx  (additivity)"],
      ]} />

      <h2 style={S.h2}>Standard Integrals</h2>
      <DiagramStandardIntegrals />
      <Table2 rows={[
        ["Power rule",        "∫xⁿ dx = xⁿ⁺¹/(n+1) + C  (n ≠ −1)\n∫x⁻¹ dx = ln|x| + C  — special case for n=−1"],
        ["Exponential",       "∫eˣ dx = eˣ + C  — only function equal to its own integral\n∫aˣ dx = aˣ/ln(a) + C"],
        ["Trig cycle",        "∫sin x dx = −cos x + C;  ∫cos x dx = sin x + C\nDifferentiation and integration cycle through sin↔cos with sign changes"],
        ["Inverse trig",      "∫1/(1+x²) dx = arctan x + C  — appears in many physics integrals\n∫1/√(1−x²) dx = arcsin x + C"],
        ["Completing square", "∫1/(x²+bx+c) dx — complete the square first\nReduces to arctan form: ∫1/(u²+a²)du = (1/a)arctan(u/a)+C"],
      ]} />

      <h2 style={S.h2}>Integration Techniques</h2>
      <DiagramTechniques />
      <Table2 rows={[
        ["u-Substitution",    "∫f(g(x))·g'(x)dx → ∫f(u)du  (let u=g(x))\nReverses the chain rule. Most common technique."],
        ["LIATE rule",        "For integration by parts, choose u in this priority:\nLogarithm > Inverse trig > Algebraic > Trig > Exponential"],
        ["By parts formula",  "∫u dv = uv − ∫v du\nSometimes requires repeated application or solving for the integral"],
        ["Partial fractions", "Decompose P(x)/Q(x) into simpler fractions\nRequires deg(P) < deg(Q); do polynomial division first if not"],
        ["Trig identities",   "sin²x = (1−cos2x)/2;  cos²x = (1+cos2x)/2\nUsed to integrate even powers of sin and cos"],
        ["Trig substitution", "√(a²−x²): x=a sin θ;  √(a²+x²): x=a tan θ;  √(x²−a²): x=a sec θ\nConverts radical integrals to trig integrals"],
        ["Improper integrals","∫ₐ^∞ f dx = lim[t→∞] ∫ₐᵗ f dx\nConverges if limit exists and is finite; otherwise diverges"],
      ]} />

      <h2 style={S.h2}>Area Between Curves</h2>
      <DiagramAreaBetweenCurves />
      <Table2 rows={[
        ["Formula",           "A = ∫ₐᵇ [f(x) − g(x)] dx  where f(x) ≥ g(x) on [a,b]\nLimits a,b are the x-coordinates of the intersection points"],
        ["Finding limits",    "Set f(x) = g(x) and solve — these are the bounds\nAlways check which function is on top by testing a point"],
        ["Curves crossing",   "If f and g cross inside [a,b], split the interval\nA = ∫ₐᶜ [top−bot] dx + ∫ᶜᵇ [top−bot] dx"],
        ["Integrating in y",  "A = ∫_c^d [right(y) − left(y)] dy\nSometimes easier when curves are better expressed as x = h(y)"],
        ["Worked example",    "f=−x²+4, g=x+2  intersect at x=−2, x=1\nA = ∫₋₂¹ (−x²−x+2)dx = [−x³/3−x²/2+2x]₋₂¹ = 9/2 = 4.5"],
      ]} />

      <h2 style={S.h2}>Applications of Integration</h2>
      <DiagramApplications />
      <Table2 rows={[
        ["Disk method",       "V = π ∫ₐᵇ [f(x)]² dx  (rotation around x-axis)\nEach cross-section is a disk of area π·r² = π·[f(x)]²"],
        ["Washer method",     "V = π ∫ₐᵇ {[f(x)]² − [g(x)]²} dx  (hollow solid)\nOuter radius f, inner radius g"],
        ["Shell method",      "V = 2π ∫ₐᵇ x·f(x) dx  (rotation around y-axis)\nEach shell: circumference 2πx, height f(x), thickness dx"],
        ["Arc length",        "L = ∫ₐᵇ √(1 + [f'(x)]²) dx\nFrom Pythagoras on infinitesimal segments: ds = √(dx²+dy²)"],
        ["Work",              "W = ∫ₐᵇ F(x) dx  (variable force along x)\nConstant force: W = F·d; spring: W = ½kx²"],
        ["Average value",     "f̄ = 1/(b−a) ∫ₐᵇ f(x) dx\nMean Value Theorem for Integrals: f attains this value at some c ∈ [a,b]"],
        ["Probability",       "P(a ≤ X ≤ b) = ∫ₐᵇ f(x) dx,  ∫₋∞^∞ f(x)dx = 1\nExpected value: E[X] = ∫x·f(x)dx"],
      ]} />

      <InfoBox
        title="Key Takeaway — Choosing the Right Approach"
        items={[
          "Indefinite integral of a standard form? → Look up the table directly.",
          "Composite function (f inside g)? → Try u-substitution first (reverses chain rule).",
          "Product of two different types? → Integration by parts; use LIATE to pick u.",
          "Rational function P/Q? → Partial fractions after checking deg(P) < deg(Q).",
          "Radical √(a²±x²) or √(x²−a²)? → Trig substitution to eliminate the square root.",
          "Definite integral of area/volume? → FTC: find antiderivative F, compute F(b)−F(a).",
          "Curves crossing? → Find intersections first, split integral, always upper minus lower.",
          "Numerical only? → Trapezoidal rule (easy) or Simpson's rule (accurate).",
        ]}
      />
    </div>
  );
}